import process from 'process/browser';
window.process = process;

import { createApp } from 'vue';
import App from './App.vue';
import router from './router/router';
import { store } from './store/store';
import axios from 'axios';
import {
    clearPendingSsoRedirectLogin,
    clearStaleMsalRedirectState,
    handleMsalRedirectOnStartup,
    hasMsalAuthResponseInUrl,
    logoutMsal,
    markPendingSsoRedirectLogin,
} from '@/auth/msal';

import { library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { far } from '@fortawesome/free-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import 'animate.css/animate.min.css';

library.add(fas, far);

switch (window.location.hostname) {
    case 'porocanje.uni-lj.si':
        axios.defaults.baseURL = 'https://porocanje.uni-lj.si/backend/api.php';
        break;
    case 'porocanje-test.uni-lj.si':
        axios.defaults.baseURL = 'https://porocanje-test.uni-lj.si/backend/api.php';
        break;
    default:
        axios.defaults.baseURL = 'http://localhost/backend/api.php';
}

axios.interceptors.request.use(function (config) {
    const token = store.getters.getToken;
    config.headers.Authorization = token;
    return config;
});

axios.interceptors.response.use(
    response => {
        if (response.data && response.data.success === false && response.data.error) {
            const errorData = response.data;

            if (errorData.error.includes('Token expired') ||
                errorData.error.includes('Invalid token') ||
                errorData.error.includes('Missing token')) {
                store.dispatch('logoutWithoutReload');
                return Promise.reject({
                    response: {
                        data: { success: false, error: 'Token expired' }
                    }
                });
            }
        }

        return response;
    },
    error => {
        if (error.code === 'ECONNABORTED' || error.name === 'CanceledError') {
            return Promise.reject(error);
        }

        console.log('GLOBAL AXIOS ERROR:', error.config?.url, error.code);
        return Promise.reject(error);
    }
);

function bootstrapApp() {
    const app = createApp(App);

    app.use(router);
    app.use(store);
    app.component('font-awesome-icon', FontAwesomeIcon);
    app.mount('#app');

    if (store.getters.getToken) {
        store.dispatch('startActivityTracking');
    }
}

function isPasswordRecoveryRoute() {
    return location.href.match(/nastavitev-gesla/) || location.href.match(/login-password/);
}

function isPublicRoute(routeName) {
    return ['login', 'login-password', 'setPassword'].includes(routeName);
}

async function redirectToLoginIfNeeded() {
    if (isPasswordRecoveryRoute() || hasMsalAuthResponseInUrl()) {
        return;
    }

    await router.isReady();

    if (!isPublicRoute(router.currentRoute.value.name)) {
        await router.replace('/login/');
    }
}

async function initApp() {
    // Process Azure redirect before Vue router can strip ?code= from the URL.
    let pendingMsalLogin = null;

    try {
        const msalRedirectResponse = await handleMsalRedirectOnStartup();
        if (msalRedirectResponse?.idToken) {
            pendingMsalLogin = msalRedirectResponse.idToken;
        }
    } catch (error) {
        console.error('MSAL redirect handling failed:', error);
        clearStaleMsalRedirectState();
    }

    // SSO redirect return: mount first so loginUserSaml can show the working modal.
    if (pendingMsalLogin) {
        markPendingSsoRedirectLogin();

        try {
            bootstrapApp();
            await router.isReady();

            const result = await store.dispatch('loginUserSaml', { token: pendingMsalLogin });

            if (!result?.success) {
                clearStaleMsalRedirectState();
                await redirectToLoginIfNeeded();
            }
        } finally {
            clearPendingSsoRedirectLogin();
        }

        return;
    }

    const token = localStorage.getItem('token');
    const expirationDate = localStorage.getItem('expirationDate');

    if (!token || !expirationDate) {
        bootstrapApp();
        await redirectToLoginIfNeeded();
        return;
    }

    if (new Date().getTime() >= Number(expirationDate)) {
        store.commit('clearAuthData');
        await logoutMsal();
        bootstrapApp();
        await redirectToLoginIfNeeded();
        return;
    }

    // Valid session: restore auth before mount so the router guard won't flash /login.
    store.commit('setToken', token);

    try {
        const res = await axios.get('?module=user&action=auto_login_user');

        if (res.data.success !== true) {
            store.commit('clearAuthData');
            await logoutMsal();
            bootstrapApp();
            await redirectToLoginIfNeeded();
            return;
        }

        store.commit('setAuthData', res);
    } catch (error) {
        console.log(error);
        bootstrapApp();
        return;
    }

    bootstrapApp();
    await router.isReady();

    if (router.currentRoute.value.name === 'login') {
        router.push('/podrocja/');
    }
}

initApp();
